import { parseISO } from 'date-fns'
import { db } from '../../../db'
import {
  useGetById,
  useGetByIds,
  useGetList,
  useGetPaginatedList,
  useGetQuery,
} from '../../../db/hooks'
import {
  TABLE_ACTIVITIES,
  TABLE_ACTIVITY_CATEGORIES,
  TABLE_ACTIVITY_GROUPS,
  TABLE_ACTIVITY_HIERARCHIES,
} from '../../../db/types'
import { SortDirection } from '../../../shared/types'
import { updateDatabase } from '../../../shared/utils/syncUtil'
import { Activity, ActivityHierarchy, Category, Group } from '../types'

const generateIndexedSearch = (activity: Activity): Array<string> => {
  if (activity.search === undefined || activity.search === null) {
    return []
  }

  return activity.search.toLocaleLowerCase().split(' ')
}

function generateIndexedSort(activity: Activity) {
  return activity.sort ?? ''
}

function generateIndexedStartDate(activity: Activity) {
  return parseISO(activity.startDate)
}

const overrideActivities = async (
  activities: Array<Activity>
): Promise<void> => {
  const enrichedActivities = activities.map((activity) => {
    return {
      ...activity,
      searchIndexed: generateIndexedSearch(activity),
      sortIndexed: generateIndexedSort(activity),
      startDateIndexed: generateIndexedStartDate(activity),
    }
  })

  await updateDatabase(db, TABLE_ACTIVITIES, enrichedActivities)
}

const overrideActivity = async (activity: Activity): Promise<void> => {
  await db.table(TABLE_ACTIVITIES).put({
    ...activity,
    searchIndexed: generateIndexedSearch(activity),
    sortIndexed: generateIndexedSort(activity),
    startDateIndexed: generateIndexedStartDate(activity),
  })
}

const overrideActivityCategories = async (
  activityCategories: Array<Category>
): Promise<void> => {
  await updateDatabase(db, TABLE_ACTIVITY_CATEGORIES, activityCategories)
}

const overrideActivityGroups = async (
  activityGroups: Array<Group>
): Promise<void> => {
  const enrichedActivityGroups = activityGroups.map((activityGroup) => {
    return {
      ...activityGroup,
      sortIndexed: activityGroup.sort ?? '',
    }
  })

  await updateDatabase(db, TABLE_ACTIVITY_GROUPS, enrichedActivityGroups)
}

const overrideActivityHierarchies = async (
  activityHierarchies: Array<ActivityHierarchy>
): Promise<void> => {
  await updateDatabase(db, TABLE_ACTIVITY_HIERARCHIES, activityHierarchies)
}

const overrideActivityHierarchy = async (
  activityHierarchy: ActivityHierarchy
): Promise<void> => {
  await db.table(TABLE_ACTIVITY_HIERARCHIES).put(activityHierarchy)
}

const deleteActivity = async (id: string): Promise<void> => {
  await db.table(TABLE_ACTIVITIES).delete(id)
}

const useGetActivitiesForProject = (projectId: string) => {
  return useGetQuery<Activity[]>(() => {
    return db
      .table<Activity>(TABLE_ACTIVITIES)
      .where('projectId')
      .equals(projectId)
      .sortBy('sortIndexed')
  }, [projectId])
}

const useGetActivitiesForAddress = (addressId: string) => {
  return useGetQuery<Activity[]>(() => {
    return db
      .table<Activity>(TABLE_ACTIVITIES)
      .where('addressId')
      .equals(addressId)
      .sortBy('sortIndexed')
  }, [addressId])
}

const useGetActivitiesByIds = (ids: string[]) => {
  return useGetByIds<Activity>(TABLE_ACTIVITIES, ids)
}

const useGetActivities = (
  search: string,
  page: number,
  size: number,
  options: {
    sortBy?: string
    sortDirection?: 'asc' | 'desc'
    filter?: (item: Activity) => boolean
  }
) =>
  useGetPaginatedList<Activity>(TABLE_ACTIVITIES, search, page, size, {
    sortBy: 'sortIndexed',
    sortDirection: 'asc',
    ...options,
  })

const useGetActivityById = (id: string) =>
  useGetById<Activity>(TABLE_ACTIVITIES, id)
const useGetAllActivities = (
  filter?: (item: Activity) => boolean,
  sortDirection?: SortDirection
) =>
  useGetList<Activity>(TABLE_ACTIVITIES, {
    filter: filter,
    sortDirection: sortDirection,
  })

const useGetActivityCategories = (filter?: (item: Category) => boolean) =>
  useGetList<Category>(TABLE_ACTIVITY_CATEGORIES, { filter: filter })
const useGetActivityGroups = () =>
  useGetList<Group>(TABLE_ACTIVITY_GROUPS, { sortBy: 'sortIndexed' })
const useGetActivityGroup = (id: string) =>
  useGetById<Group>(TABLE_ACTIVITY_GROUPS, id)

export {
  deleteActivity,
  overrideActivities,
  overrideActivity,
  overrideActivityCategories,
  overrideActivityGroups,
  overrideActivityHierarchies,
  overrideActivityHierarchy,
  useGetActivities,
  useGetActivitiesByIds,
  useGetActivitiesForAddress,
  useGetActivitiesForProject,
  useGetActivityById,
  useGetActivityCategories,
  useGetActivityGroup,
  useGetActivityGroups,
  useGetAllActivities,
}
